import React from 'react';
import transition from '../../transition';
import CaseStudy from '../CaseStudy/CaseStudy';
import '../Project.css';

import hero from '../../assets/images/projects/igws/igws-hero.png';
import desktop18 from '../../assets/images/projects/igws-hd/desktop18.png';
import desktop14 from '../../assets/images/projects/igws-hd/desktop14.png';
import desktop8 from '../../assets/images/projects/igws-hd/desktop8.png';
import desktop16 from '../../assets/images/projects/igws-hd/desktop16.png';
import phone5 from '../../assets/images/projects/igws-hd/phone5.png';
import gdmsDesktop from '../../assets/images/projects/igws-hd/gdms-desktop.png';
import gdmsWellDetail from '../../assets/images/projects/igws-hd/gdms-well-detail.png';
import gdmsSearch from '../../assets/images/projects/igws-hd/gdms-search.png';
import gdmsMobile from '../../assets/images/projects/igws-hd/gdms-mobile.png';
import desktop12 from '../../assets/images/projects/igws/desktop12.png';
import desktop15 from '../../assets/images/projects/igws/desktop15.png';
import desktop2 from '../../assets/images/projects/igws/desktop2.png';
import desktop6 from '../../assets/images/projects/igws/desktop6.png';
import desktop17 from '../../assets/images/projects/igws/desktop17.png';

const IGWS = () => {
  return (
    <CaseStudy
      className="igws"
      accent="var(--igws)"
      eyebrow="Institutional · Site, apps, data"
      title="IGWS"
      summary="I rebuilt the public website, internal tools, and statewide well database for Indiana’s Geological & Water Survey."
      links={[
        { href: 'https://igws.iu.edu/', label: 'View live site' },
        { href: 'https://igws.iu.edu/gdms', label: 'Open GDMS' },
        { href: '#frames', label: 'View frames' }
      ]}
      hero={{
        src: hero,
        alt: 'IGWS homepage composition over geological strata',
        caption: 'Indiana Geological & Water Survey'
      }}
      date="Jul. 2022 – 2025"
      role="Web developer on the Survey’s Info Services team"
      stack={['React.js', 'Node.js', 'ArcGIS', 'MySQL']}
      brief={{
        label: 'Background',
        they: {
          title: 'The Survey',
          body: 'The Indiana Geological & Water Survey is a research institute at Indiana University. They study the state’s geology, publish hazard and resource information, and maintain records that planners, drillers, teachers, and homeowners use: wells, rock cores, minerals, water, and energy.'
        },
        me: {
          title: 'My role',
          body: 'I was their web developer. I led the public website rebuild, built the content system researchers use to publish, rebuilt older internal apps onto a modern stack, and shipped new tools like an environmental assessment app. I also co-built GDMS, a free statewide search over well and geologic records.'
        }
      }}
      problem={{
        title: 'Public science stuck behind outdated software.',
        lead: 'People couldn’t find hazards or maps on their phones. Staff needed IT help to update a page. Companies paid $500 a year and still waited about 45 seconds for a single well search.',
        detail:
          'The old website ran on ColdFusion and had grown past 5,000 pages, with confusing navigation and poor mobile support. Their petroleum / well database was a separate paid product: slow, limited, and locked away from the public. IGWS exists to share geologic knowledge. The tools made that hard.'
      }}
      metrics={[
        { value: '4×', label: 'Site traffic after launch' },
        { value: '5k → 35', label: 'Public pages after the cut' },
        { value: '195k+', label: 'Well & site records in GDMS' },
        { value: '<1s', label: 'Search time (was ~45s)' }
      ]}
      outcome="Half a million visits later, Indiana’s geological survey finally feels like a modern public site."
      solutionsLabel="What shipped"
      solutions={[
        {
          title: 'Public website · 2023',
          lead: 'Replaced the ColdFusion site and cut thousands of unused pages down to what people actually visited.',
          detail: (
            <>
              Working with a staff content group, high-traffic pages stayed on the live site and the
              rest moved into an archive called Digital Collections. The new site launched with about
              35 focused pages.{' '}
              <a
                href="https://igws.iu.edu/news/story/2023-newwebsite"
                target="_blank"
                rel="noreferrer"
              >
                Read the Survey’s launch story
              </a>
              . Making the site accessible was part of their strategic plan, and it shipped with the
              redesign.
            </>
          ),
          images: [
            {
              src: desktop18,
              alt: 'IGWS homepage',
              caption: 'Homepage paths into hazards, water, energy, and minerals.'
            },
            {
              src: phone5,
              alt: 'IGWS homepage mobile',
              caption: 'The same structure, usable on a phone.'
            },
            {
              src: desktop15,
              alt: 'Physical collections',
              caption: 'Collections content that explains what the Survey holds.'
            }
          ]
        },
        {
          title: 'Publishing tools for staff',
          lead: 'About 40 researchers and editors can update the site themselves.',
          detail:
            'I built a custom content management system with roles, research uploads, public alerts, and flexible page layouts. It matched how the Survey’s team already worked, instead of forcing them into a generic blog tool.',
          images: [
            {
              src: desktop8,
              alt: 'Admin CMS editor',
              caption: 'Page editor staff can use without waiting on a developer.'
            },
            {
              src: desktop2,
              alt: 'User permissions admin',
              caption: 'Permissions tied to real staff roles.'
            },
            {
              src: desktop6,
              alt: 'Staff spotlight editor',
              caption: 'Block editor: reorder sections, preview, publish.'
            },
            {
              src: desktop14,
              alt: 'Searchable CMS cards',
              caption: 'Admin tools for day-to-day content work.'
            }
          ]
        },
        {
          title: 'Apps & tools',
          lead: 'Rebuilt older internal apps and built new ones people actually use.',
          detail:
            'The public site was only part of the job. I moved legacy tools onto React and built new workflows from scratch. One example is the environmental assessment tool: enter a project, choose a map location, pull geologic layers, and generate an assessment without opening separate desktop mapping software.',
          images: [
            {
              src: desktop16,
              alt: 'Environmental assessment map',
              caption: 'Environmental assessment: locate the site, load map layers.'
            },
            {
              src: desktop17,
              alt: 'Environmental assessment form',
              caption: 'Project details before the map step.'
            },
            {
              src: desktop12,
              alt: 'Geospatial data portals',
              caption: 'Public data portals the site and apps both link to.'
            }
          ]
        },
        {
          title: 'GDMS · 2025',
          lead: 'A free statewide search over 195,000+ locations. Queries that used to take ~45 seconds now return right away.',
          detail: (
            <>
              I helped rebuild the old paid Petroleum Database Management System into{' '}
              <a href="https://igws.iu.edu/gdms" target="_blank" rel="noreferrer">
                GDMS
              </a>{' '}
              (Geological Database Management System),{' '}
              <a
                href="https://igws.iu.edu/news/story/2025-GDMS"
                target="_blank"
                rel="noreferrer"
              >
                the Survey’s subsurface data hub
              </a>
              . It covers oil and gas, water wells, coal, boreholes, rock outcrops, and core photos.
              Same kinds of records people used to pay to access. Now free, and fast.
            </>
          ),
          images: [
            {
              src: gdmsDesktop,
              alt: 'GDMS wells search with 160k+ results',
              caption: 'Statewide well search — 160k+ records, download included.'
            },
            {
              src: gdmsWellDetail,
              alt: 'GDMS well record detail with events and data categories',
              caption: 'Open a well: details, events, docs, and available data.'
            },
            {
              src: gdmsSearch,
              alt: 'GDMS advanced filters for township, county, and depth',
              caption: 'Township, county, depth, and other filters built in.'
            },
            {
              src: gdmsMobile,
              alt: 'GDMS wells results on a phone',
              caption: 'Same search on a phone in the field.'
            }
          ]
        }
      ]}
      decisions={[
        {
          title: 'Ship fewer pages',
          body: 'Kept pages with real traffic (about 2,000 views a year or more) and archived the rest. A smaller site is easier to use and easier to maintain.'
        },
        {
          title: 'Build publishing tools for staff',
          body: 'Researchers needed to update pages, alerts, and uploads themselves. A custom editor with real permission levels beat a generic blog CMS.'
        },
        {
          title: 'Rebuild the tools, not just the homepage',
          body: 'Older apps moved onto the modern stack. New ones, like the environmental assessment tool, replaced slow desktop-GIS and email workflows.'
        },
        {
          title: 'One free subsurface database',
          body: 'GDMS was not a rename of the old petroleum database. Oil, water, coal, and outcrop data live in one searchable system, with Excel downloads and document previews people had asked for for years.'
        }
      ]}
      next={{ link: '/projects/menumaker', label: 'Menu Maker' }}
    />
  );
};

export default transition(IGWS);
